// frontend/src/components/Rider/AddressAutocomplete.jsx
// Fully migrated to Places API (New) - uses AutocompleteSuggestion and Place APIs
import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps } from '../../utils/loadGoogleMaps';

const AddressAutocomplete = ({ value, onChange, onPlaceSelect, placeholder, required, name }) => {
  const inputRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    let pollInterval = null;

    const initializeServices = () => {
      if (!mounted) return false;

      // Check if Google Maps is fully loaded with NEW API
      if (window.google && window.google.maps && window.google.maps.places) {
        try {
          console.log('✅ Google Maps loaded, Places API (New) ready');
          setIsInitialized(true);
          return true;
        } catch (error) {
          console.error('❌ Error checking Places API:', error);
          return false;
        }
      }
      return false;
    };

    loadGoogleMaps()
      .then(() => {
        if (!mounted) return;
        
        console.log('📍 Google Maps script loaded, checking Places API...');
        
        // Try to initialize immediately
        if (initializeServices()) {
          return;
        }

        // If not ready, poll every 100ms for up to 5 seconds
        let attempts = 0;
        const maxAttempts = 50;
        
        pollInterval = setInterval(() => {
          attempts++;
          
          if (initializeServices()) {
            clearInterval(pollInterval);
            pollInterval = null;
          } else if (attempts >= maxAttempts) {
            console.error('❌ Timeout waiting for Google Maps Places library');
            clearInterval(pollInterval);
            pollInterval = null;
          }
        }, 100);
      })
      .catch((error) => {
        console.error('❌ Failed to load Google Maps:', error);
      });

    return () => {
      mounted = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const fetchSuggestions = async (inputValue) => {
    if (!isInitialized) {
      console.log('Services not ready yet');
      return;
    }

    if (!inputValue || inputValue.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);

    try {
      // Use the NEW AutocompleteSuggestion API
      const request = {
        input: inputValue,
        includedRegionCodes: ['us'], // Restrict to US (correct format)
      };

      const { suggestions: autocompleteSuggestions } = 
        await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      if (autocompleteSuggestions && autocompleteSuggestions.length > 0) {
        setSuggestions(autocompleteSuggestions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Autocomplete error:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    onChange(inputValue);
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(inputValue);
    }, 300);
  };

  const handleSelectSuggestion = async (suggestion) => {
    if (!isInitialized) {
      console.error('Places API not initialized');
      return;
    }

    try {
      // Use the NEW Place API to get details
      const place = suggestion.placePrediction.toPlace();

      await place.fetchFields({
        fields: ['displayName', 'formattedAddress', 'location'],
      });

      // Use formattedAddress from the new API
      const address = place.formattedAddress || place.displayName;
      onChange(address);

      // Call onPlaceSelect if provided (for setting coordinates)
      if (onPlaceSelect && place.location) {
        // Create a place object compatible with the old Google Maps API format
        // In the new Places API, location.lat() and location.lng() are methods
        const placeObject = {
          formatted_address: address,
          geometry: {
            location: {
              lat: () => place.location.lat(),
              lng: () => place.location.lng()
            }
          }
        };
        onPlaceSelect(placeObject);
      }

      setShowSuggestions(false);
      setSuggestions([]);
    } catch (error) {
      console.error('Error fetching place details:', error);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const handleFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        name={name}
        value={value || ''}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        required={required}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        autoComplete="off"
      />
      
      {isLoading && (
        <div className="absolute right-3 top-3">
          <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      )}
      
      {!isInitialized && value && value.length >= 3 && (
        <div className="absolute right-3 top-3 text-xs text-gray-500">
          Initializing...
        </div>
      )}
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion, index) => {
            const prediction = suggestion.placePrediction;
            const mainText = prediction.structuredFormat?.mainText?.text || 
                           prediction.text?.text || 
                           'Address';
            const secondaryText = prediction.structuredFormat?.secondaryText?.text || '';
            
            return (
              <div
                key={prediction.placeId || index}
                onClick={() => handleSelectSuggestion(suggestion)}
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
              >
                <div className="font-medium text-sm text-gray-900">{mainText}</div>
                {secondaryText && (
                  <div className="text-xs text-gray-600 mt-0.5">{secondaryText}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;